/**
 * Fast media upload primitives — shared by /mediaupload (mobile) and
 * /portal/shooter (desktop). Everything is direct-to-S3 with retry +
 * parallelism, and photos get downscaled + re-encoded client-side before
 * upload so a 6 MB iPhone photo becomes ~600 KB.
 *
 * Public API:
 *   compressImage(file, maxEdge?, quality?) → File
 *   uploadPhotoFast({ file, onProgress? }) → { item_id, size }
 *   uploadVideoFast({ file, onProgress? }) → { video_id, size }
 */
import { api } from "@/lib/api";

const CHUNK_SIZE = 5 * 1024 * 1024;
const CHUNK_CONCURRENCY = 6;
const MAX_RETRIES = 4;

const PHOTO_MAX_EDGE = 2400;
const PHOTO_QUALITY = 0.85;

export async function uploadPhotoFast({ file: original, onProgress }) {
  let file = original;
  try {
    if (original.size > 800 * 1024) {
      const compressed = await compressImage(original, PHOTO_MAX_EDGE, PHOTO_QUALITY);
      if (compressed && compressed.size < original.size) file = compressed;
    }
  } catch { /* fall through with original */ }
  const ct = file.type || "image/jpeg";
  const init = await api.post("/marketplace/items/photo-init", { content_type: ct });
  const { item_id, upload_url } = init.data;
  await putWithRetry(upload_url, file, ct, onProgress);
  return { item_id, size: file.size };
}

export async function uploadVideoFast({ file, onProgress }) {
  const ct = file.type || "video/mp4";
  return uploadMultipartFast({
    file,
    onProgress,
    init: () => api.post("/box/videos/initiate-upload", {
      filename: file.name, file_size: file.size, content_type: ct, duration: 0,
    }),
    partUrl: ({ video_id, upload_id }, part_number) =>
      api.post("/box/videos/upload-part-url", { video_id, upload_id, part_number }),
    complete: ({ video_id, upload_id }, parts) =>
      api.post("/box/videos/complete-upload", { video_id, upload_id, parts }),
  });
}

// Keeps parts at 5 MB, growing only when needed to stay under S3's 10,000-part cap.
function chunkSizeFor(size) {
  const mb = 1024 * 1024;
  return Math.max(CHUNK_SIZE, Math.ceil(size / 9000 / mb) * mb);
}

export async function uploadMultipartFast({ file, onProgress, init, partUrl, complete }) {
  const ct = file.type || "video/mp4";
  const chunkSize = chunkSizeFor(file.size);
  const parts = Math.max(1, Math.ceil(file.size / chunkSize));

  const initRes = await init();
  const session = initRes.data;
  const { video_id } = session;

  const partProgress = new Array(parts).fill(0);
  const partSizes = new Array(parts).fill(0);
  const etags = new Array(parts).fill(null);
  const reportProgress = () => {
    if (!onProgress) return;
    let done = 0;
    for (let i = 0; i < parts; i++) done += (partProgress[i] / 100) * partSizes[i];
    onProgress(Math.min(99.9, (done / file.size) * 100));
  };

  const uploadPart = async (idx) => {
    const start = idx * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    const blob = file.slice(start, end);
    partSizes[idx] = blob.size;
    const urlRes = await withApiRetry(() => partUrl(session, idx + 1), 3);
    const put = await putWithRetry(urlRes.data.url, blob, ct, (p) => {
      partProgress[idx] = p;
      reportProgress();
    });
    etags[idx] = { PartNumber: idx + 1, ETag: put.etag };
    partProgress[idx] = 100;
    reportProgress();
  };

  const queue = Array.from({ length: parts }, (_, i) => i);
  await Promise.all(
    Array.from({ length: Math.min(CHUNK_CONCURRENCY, parts) }, async () => {
      while (queue.length) {
        const idx = queue.shift();
        // eslint-disable-next-line no-await-in-loop
        await uploadPart(idx);
      }
    }),
  );

  await withApiRetry(() => complete(session, etags), 3);
  return { video_id, size: file.size };
}

export function compressImage(file, maxEdge = PHOTO_MAX_EDGE, quality = PHOTO_QUALITY) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      URL.revokeObjectURL(url);
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const longest = Math.max(iw, ih);
      const scale = longest > maxEdge ? maxEdge / longest : 1;
      const w = Math.round(iw * scale);
      const h = Math.round(ih * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas.toBlob failed"));
          const jpg = new File(
            [blob],
            (file.name || "photo").replace(/\.[a-z0-9]+$/i, "") + ".jpg",
            { type: "image/jpeg", lastModified: Date.now() },
          );
          resolve(jpg);
        },
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Browser cannot decode this image format"));
    };
    img.src = url;
  });
}

async function putWithRetry(url, blob, contentType, onProgress) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      return await xhrPut(url, blob, contentType, onProgress);
    } catch (e) {
      lastErr = e;
      if (e.permanent) break;
      if (onProgress) onProgress(0);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt)));
    }
  }
  throw lastErr || new Error("Upload failed after retries");
}

async function withApiRetry(fn, attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try { return await fn(); }
    catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      if (status && status >= 400 && status < 500) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 500 * Math.pow(2, i)));
    }
  }
  throw lastErr;
}

function xhrPut(url, blob, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.timeout = 5 * 60 * 1000;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = (xhr.getResponseHeader("ETag") || "").replaceAll('"', "");
        resolve({ etag });
      } else {
        const err = new Error(`S3 PUT failed (${xhr.status})`);
        if (xhr.status >= 400 && xhr.status < 500) err.permanent = true;
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error("Network error uploading to S3"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.ontimeout = () => reject(new Error("Upload timed out — check signal and retry"));
    xhr.send(blob);
  });
}
